import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { HexColorPicker } from "react-colorful";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const presets = [
  "#FFFFFF",
  "#F5F5F5",
  "#E5E7EB",
  "#D1D5DB",
  "#9CA3AF",
  "#374151",
  "#DBEAFE",
  "#3B82F6",
  "#1D4ED8",
  "#DCFCE7",
  "#86EFAC",
  "#22C55E",
  "#FEF3C7",
  "#F59E0B",
  "#EA580C",
  "#FEE2E2",
  "#EF4444",
  "#DC2626",
  "#F3E8FF",
  "#C084FC",
];

function normalizeHex(value) {
  if (/^#[0-9a-f]{6}$/i.test(value || "")) return value.toUpperCase();
  if (/^#[0-9a-f]{3}$/i.test(value || "")) {
    return `#${value
      .slice(1)
      .split("")
      .map((character) => character.repeat(2))
      .join("")}`.toUpperCase();
  }
  return "#F5F5F5";
}

export function ColorPickerCustom({ onChange, value, defaultValue, className }) {
  const [color, setColor] = useState(
    normalizeHex(value || defaultValue || "#F5F5F5"),
  );

  useEffect(() => {
    if (value) setColor(normalizeHex(value));
  }, [value]);

  function handleChange(newColor) {
    setColor(newColor);
    onChange?.(newColor);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-14 w-full touch-manipulation items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 text-left text-base shadow-sm outline-none transition-colors hover:border-neutral-300 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-blue-500/40 active:scale-[0.99]",
            className,
          )}
        >
          <span
            className="h-7 w-7 shrink-0 rounded-lg border border-black/10 shadow-sm"
            style={{ backgroundColor: color }}
          />
          <span className="font-medium uppercase text-neutral-700">{color}</span>
          <Icon icon="mdi:chevron-down" className="ml-auto text-xl text-neutral-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(19rem,calc(100vw-2rem))] p-4"
        align="start"
        sideOffset={8}
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-neutral-800">Elige un color</span>
          <span
            className="rounded-md px-2 py-1 text-xs font-semibold uppercase"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {color}
          </span>
        </div>
        <div className="[&_.react-colorful]:h-40 [&_.react-colorful]:w-full">
          <HexColorPicker color={color} onChange={handleChange} />
        </div>
        <div className="mt-4 grid grid-cols-5 gap-2">
          {presets.map((preset) => (
            <button
              key={preset}
              type="button"
              aria-label={`Usar color ${preset}`}
              onClick={() => handleChange(preset)}
              className={cn(
                "h-11 w-11 touch-manipulation rounded-xl border border-black/10 shadow-sm transition-transform hover:scale-105 active:scale-95",
                color.toLowerCase() === preset.toLowerCase() &&
                  "ring-2 ring-blue-500 ring-offset-2",
              )}
              style={{ backgroundColor: preset }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
